import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { AttachmentType } from '@/entities/consignments';
import { useUploadReceiveShipmentAttachmentsMutation } from '@/entities/receive';
import { getApiErrorMessage, useProjectSiteParams } from '@/shared/lib';

/**
 * The add-attachment dialog of the history tab: which receipt is open, and the
 * upload itself.
 *
 * One file per submit, like the consignment dialog it reuses — the endpoint
 * takes several, but the dialog offers one, and its answer carries the whole
 * list anyway. The `Receive` tag invalidation is what puts the new file into the
 * row.
 */
export const useAttachmentUploadDialog = () => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const [shipmentId, setShipmentId] = useState<string | null>(null);
  const [uploadAttachments, { isLoading: isUploading }] = useUploadReceiveShipmentAttachmentsMutation();

  const close = useCallback(() => setShipmentId(null), []);

  const upload = useCallback(
    async (file: File, attachmentType: AttachmentType, comment?: string) => {
      if (!shipmentId) return;

      try {
        await uploadAttachments({
          shipmentId,
          projectId,
          siteId,
          files: [file],
          attachmentTypes: [attachmentType],
          comment
        }).unwrap();
        toast.success(t('receive.attachments.uploadSuccess', 'Document attached to the receipt'));
        close();
      } catch (error) {
        toast.error(
          getApiErrorMessage(
            error,
            t('receive.attachments.error.upload', 'Could not attach {{file}}', { file: file.name })
          )
        );
      }
    },
    [shipmentId, uploadAttachments, projectId, siteId, t, close]
  );

  return { shipmentId, open: setShipmentId, close, upload, isUploading };
};
